import { useState, useEffect } from 'react';
import { getAllApplications, approveApplication, rejectApplication, disbursePayment } from '../../services/api';

function Badge({ status }) {
  const map = { APPROVED:{bg:'#d1fae5',c:'#065f46'}, PENDING:{bg:'#fef3c7',c:'#92400e'}, DISBURSED:{bg:'#dbeafe',c:'#1e40af'}, REJECTED:{bg:'#fee2e2',c:'#7f1d1d'}, FRAUD_DETECTED:{bg:'#fee2e2',c:'#7f1d1d'} };
  const s = map[status]||{bg:'#f3f4f6',c:'#374151'};
  return <span style={{ background:s.bg, color:s.c, borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{status}</span>;
}

export default function Applications() {
  const [apps, setApps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('All');
  const [selected, setSelected] = useState(null);
  const [modal, setModal]       = useState(null);
  const [toast, setToast]       = useState('');
  const [acting, setActing]     = useState(false);

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),3200); };

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const data = await getAllApplications(); setApps(data); }
    catch(err) { showToast('Failed: '+err.message); }
    finally { setLoading(false); }
  };

  const handleApprove = async (a) => {
    setActing(true);
    try {
      await approveApplication(a.id);
      setApps(x=>x.map(r=>r.id===a.id?{...r,status:'APPROVED'}:r));
      setModal({ name:a.fullName, email:a.email, credId:'IJ-'+a.id, credPw:a.fullName?.slice(0,4)+'@'+Math.floor(1000+Math.random()*9000) });
      setSelected(null);
    } catch(err) { showToast('Error: '+err.message); } finally { setActing(false); }
  };

  const handleReject = async (id) => {
    setActing(true);
    try { await rejectApplication(id,'Does not meet eligibility criteria'); setApps(x=>x.map(r=>r.id===id?{...r,status:'REJECTED'}:r)); showToast('Application rejected.'); setSelected(null); }
    catch(err) { showToast('Error: '+err.message); } finally { setActing(false); }
  };

  const handleDisburse = async (id) => {
    setActing(true);
    try { await disbursePayment(id); setApps(x=>x.map(r=>r.id===id?{...r,status:'DISBURSED'}:r)); showToast('Payment disbursed.'); setSelected(null); }
    catch(err) { showToast('Error: '+err.message); } finally { setActing(false); }
  };

  const counts = { All:apps.length, PENDING:0, APPROVED:0, DISBURSED:0, REJECTED:0 };
  apps.forEach(a=>{ if(counts[a.status]!==undefined) counts[a.status]++; });
  const filtered = apps.filter(a=>(filter==='All'||a.status===filter)&&(a.fullName?.toLowerCase().includes(search.toLowerCase())||a.email?.toLowerCase().includes(search.toLowerCase())||String(a.id).includes(search)));

  return (
    <div>
      {toast && <div style={{ position:'fixed', top:72, right:24, zIndex:9999, background:'#1f2937', color:'white', padding:'12px 20px', borderRadius:10, fontSize:13, fontWeight:600, boxShadow:'0 4px 20px rgba(0,0,0,0.3)' }}>{toast}</div>}

      {modal && (
        <div style={{ position:'fixed', inset:0, zIndex:998, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:14, padding:32, maxWidth:440, width:'100%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ textAlign:'center', marginBottom:22 }}>
              <div style={{ fontSize:50, marginBottom:10 }}>?</div>
              <h2 style={{ fontSize:19, fontWeight:800, color:'#065f46', margin:'0 0 6px' }}>Application Approved!</h2>
              <p style={{ color:'var(--text-secondary)', fontSize:13, margin:0 }}>Login credentials generated and emailed to applicant.</p>
            </div>
            <div style={{ background:'var(--bg-light)', borderRadius:10, padding:'12px 16px', marginBottom:14 }}>
              <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:5 }}>Applicant</div>
              <div style={{ fontWeight:700 }}>{modal.name}</div>
              <div style={{ fontSize:12, color:'var(--text-light)' }}>{modal.email}</div>
            </div>
            <div style={{ background:'#eff6ff', border:'1.5px solid #0066cc', borderRadius:10, padding:'14px 16px', marginBottom:20 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#0066cc', marginBottom:10 }}>Credentials sent to {modal.email}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[['Beneficiary ID',modal.credId],['Password',modal.credPw]].map(([k,v])=>(
                  <div key={k}><div style={{ fontSize:11, color:'var(--text-light)', marginBottom:3 }}>{k}</div><div style={{ fontFamily:'monospace', fontWeight:800, fontSize:15, color:'#0066cc' }}>{v}</div></div>
                ))}
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setModal(null)} style={{ flex:1, padding:12, background:'linear-gradient(135deg,#0066cc,#0052a3)', color:'white', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>Done</button>
              <button onClick={()=>{ navigator.clipboard?.writeText('ID: '+modal.credId+' Password: '+modal.credPw); showToast('Copied!'); }} style={{ padding:'12px 16px', background:'var(--bg-light)', border:'1px solid var(--border-light)', borderRadius:8, fontWeight:600, cursor:'pointer' }}>Copy</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {Object.entries(counts).map(([s,n])=>(
          <button key={s} onClick={()=>setFilter(s)} style={{ padding:'7px 16px', borderRadius:20, fontSize:13, cursor:'pointer', fontWeight:700, border:'none', background:filter===s?'var(--primary-color)':'white', color:filter===s?'white':'var(--text-secondary)', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
            {s} ({n})
          </button>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:18 }}>
        <input type="text" placeholder="Search name, email or ID..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1, padding:'10px 14px', border:'1.5px solid var(--border-light)', borderRadius:8, fontSize:14 }}/>
        <button onClick={load} style={{ padding:'10px 20px', background:'var(--primary-color)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>Refresh</button>
      </div>

      {loading ? <div style={{ textAlign:'center', padding:60, color:'var(--text-secondary)' }}>? Loading...</div> : (
        <div style={{ display:'grid', gridTemplateColumns:selected?'1fr 300px':'1fr', gap:18 }}>
          <div style={{ background:'white', borderRadius:12, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', overflow:'hidden' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead><tr style={{ background:'var(--bg-light)' }}>
                  {['ID','Applicant','County','Category','Status','Date',''].map(h=><th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:700, color:'var(--text-secondary)', fontSize:11, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {filtered.map(a=>(
                    <tr key={a.id} onClick={()=>setSelected(selected===a.id?null:a.id)} style={{ borderBottom:'1px solid var(--border-light)', cursor:'pointer', background:selected===a.id?'var(--primary-light)':'transparent' }}>
                      <td style={{ padding:'12px 14px', fontFamily:'monospace', fontSize:11, color:'var(--text-light)' }}>APP-{a.id}</td>
                      <td style={{ padding:'12px 14px' }}><div style={{ fontWeight:700 }}>{a.fullName}</div><div style={{ fontSize:11, color:'var(--text-light)' }}>{a.email}</div></td>
                      <td style={{ padding:'12px 14px', color:'var(--text-secondary)' }}>{a.county}</td>
                      <td style={{ padding:'12px 14px', color:'var(--text-secondary)', fontSize:12 }}>{a.category}</td>
                      <td style={{ padding:'12px 14px' }}><Badge status={a.status}/></td>
                      <td style={{ padding:'12px 14px', color:'var(--text-light)', fontSize:12 }}>{a.createdAt?.split('T')[0]||'�'}</td>
                      <td style={{ padding:'12px 14px', color:'#0066cc', fontSize:16 }}>�</td>
                    </tr>
                  ))}
                  {filtered.length===0 && <tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:'var(--text-light)' }}>No applications found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {selected && (()=>{
            const a = apps.find(x=>x.id===selected);
            if (!a) return null;
            return (
              <div style={{ background:'white', borderRadius:12, boxShadow:'0 2px 8px rgba(0,0,0,0.07)', padding:20, alignSelf:'flex-start', position:'sticky', top:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                  <div style={{ fontWeight:800, fontSize:14 }}>Detail</div>
                  <button onClick={()=>setSelected(null)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-light)' }}>x</button>
                </div>
                <div style={{ marginBottom:12 }}><Badge status={a.status}/></div>
                {(()=>{
                  const j=a.justification||'';
                  const parts=Object.fromEntries(j.split('|').map(p=>{const i=p.indexOf(':');return i>-1?[p.slice(0,i).trim(),p.slice(i+1).trim()]:['','']}));
                  return [['ID','APP-'+a.id],['Name',a.fullName],['Email',a.email],['Phone',a.phone||'�'],['County',a.county],['Category',a.category],['Date',a.createdAt?.split('T')[0]||'�'],['Business',parts['Business']||'�'],['Loan Purpose',parts['Purpose']||'�'],['Repayment',parts['Repay']||'�'],['Payment',parts['Pay']||'�']];
                })().map(([k,v])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border-light)', fontSize:12 }}>
                    <span style={{ color:'var(--text-secondary)' }}>{k}</span>
                    <span style={{ fontWeight:700, textAlign:'right', maxWidth:150, wordBreak:'break-all' }}>{v||'�'}</span>
                  </div>
                ))}
                <div style={{ marginTop:16, display:'grid', gap:8 }}>
                  {a.status==='PENDING' && <>
                    <button onClick={()=>handleApprove(a)} disabled={acting} style={{ padding:11, background:'linear-gradient(135deg,#10b981,#059669)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:acting?0.6:1 }}>{acting?'Processing...':'Approve and Send Login'}</button>
                    <button onClick={()=>handleReject(a.id)} disabled={acting} style={{ padding:11, background:'transparent', color:'#ef4444', border:'2px solid #ef4444', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:acting?0.6:1 }}>Reject</button>
                  </>}
                  {a.status==='APPROVED' && a.loanStatus==='FEE_PAID' && <button onClick={()=>handleDisburse(a.id)} disabled={acting} style={{ padding:11, background:'linear-gradient(135deg,#10b981,#059669)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', opacity:acting?0.6:1 }}>{acting?'Processing...':'Disburse Loan'}</button>}
                  {a.status==='APPROVED' && !a.loanStatus && <div style={{ padding:10, background:'#f1f5f9', borderRadius:8, fontSize:12, color:'#64748b', textAlign:'center' }}>Awaiting fee payment</div>}
                  {a.status==='APPROVED' && a.loanStatus==='PENDING_FEE' && <div style={{ padding:10, background:'#fff8e6', borderRadius:8, fontSize:12, color:'#92400e', textAlign:'center' }}>Payment in progress...</div>}
                  {a.status==='APPROVED' && a.loanStatus==='PAYMENT_FAILED' && <div style={{ padding:10, background:'#fee2e2', borderRadius:8, fontSize:12, color:'#7f1d1d', textAlign:'center' }}>Payment failed � user retry needed</div>}
                  {a.status==='DISBURSED' && <div style={{ padding:12, background:'#d1fae5', borderRadius:8, fontSize:12, color:'#065f46', fontWeight:600, textAlign:'center' }}>Payment disbursed</div>}
                  {a.status==='REJECTED' && <div style={{ padding:12, background:'#fee2e2', borderRadius:8, fontSize:12, color:'#7f1d1d', fontWeight:600, textAlign:'center' }}>Application rejected</div>}
                  {(a.idDocumentUrl||a.passportPhotoUrl||a.businessPermitUrl) && (
                    <div style={{ marginTop:12, padding:'12px', background:'#f8fafc', borderRadius:8, border:'1px solid #e2e8f0' }}>
                      <p style={{ fontSize:11, fontWeight:700, color:'#64748b', margin:'0 0 8px', textTransform:'uppercase' }}>Uploaded Documents</p>
                      <div style={{ display:'grid', gap:6 }}>
                        {a.idDocumentUrl && <a href={'https://imalishawelfare.com'+a.idDocumentUrl} target='_blank' rel='noreferrer' style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', background:'#eff6ff', borderRadius:6, fontSize:12, fontWeight:600, color:'#0066cc', textDecoration:'none' }}>National ID Document</a>}
                        {a.passportPhotoUrl && <a href={'https://imalishawelfare.com'+a.passportPhotoUrl} target='_blank' rel='noreferrer' style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', background:'#f0fdf4', borderRadius:6, fontSize:12, fontWeight:600, color:'#16a34a', textDecoration:'none' }}>Passport Photo</a>}
                        {a.businessPermitUrl && <a href={'https://imalishawelfare.com'+a.businessPermitUrl} target='_blank' rel='noreferrer' style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 10px', background:'#fefce8', borderRadius:6, fontSize:12, fontWeight:600, color:'#ca8a04', textDecoration:'none' }}>Business Permit</a>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}





